export const CHECKOUT_URL = 'https://renal-en.impultienda.ar/checkout/'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/ebQhMFajpFk3TZG64ZciM5/RENAL-EN.png'

export const DOCTOR_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/c2454c12-408a-4dc3-b109-970b755d3d37.jpg'

export const stats = [
  { value: '300+', label: 'Clinical Pages' },
  { value: '6', label: 'Specialized Volumes' },
  { value: '5', label: 'Premium Bonuses' },
  { value: '3 min', label: 'Per Algorithm' },
  { value: '1,000+', label: 'Physicians' },
]

export const trustBadges = [
  {
    title: 'Based on KDIGO',
    description: 'Evidence-based clinical framework',
  },
  {
    title: 'High-Quality PDFs',
    description: 'Designed for digital use and printing',
  },
  {
    title: 'Ready to Use Today',
    description: 'Built for real clinical situations',
  },
]

export const beforeItems = [
  'You make decisions with uncertainty.',
  'You spend more time searching for answers than actually treating the patient.',
  'You open KDIGO, UpToDate, and multiple guidelines — and still end up wondering whether you’re making the right call.',
  'You don’t know whether a decline in eGFR is truly concerning or something that can safely be monitored.',
  'You stop medications out of fear, even when they may be providing renal protection.',
  'You refer patients “just to be safe,” without knowing whether specialist evaluation is actually necessary.',
]

export const afterItems = [
  'You open the right algorithm and follow a structured decision pathway.',
  'You identify the patient’s CKD stage in minutes.',
  'You know what information to order and how to interpret it.',
  'You know what to initiate, continue, adjust, or reassess.',
  'You know what needs to be monitored — and when.',
  'You know when nephrology referral is appropriate and when the patient can continue under your care.',
]

export type PainPoint = {
  emoji: string
  title: string
  lines: string[]
}

export const painPoints: PainPoint[] = [
  {
    emoji: '😰',
    title: 'You see an elevated creatinine',
    lines: [
      'And you’re not sure whether you’re looking at an urgent problem or a finding that can safely wait.',
      'You worry about missing progressive kidney disease — or overreacting to an isolated laboratory result.',
    ],
  },
  {
    emoji: '⏱️',
    title: 'What should take minutes consumes half the visit',
    lines: [
      'You jump between different guidelines looking for one practical answer while your patient is waiting for a decision.',
      'You know the information exists. You just don’t have time to hunt for it.',
    ],
  },
  {
    emoji: '💊',
    title: 'Every medication creates another question',
    lines: [
      'You’re not sure whether to continue a medication that may be protecting kidney function — or whether keeping it could create unnecessary risk.',
      'ACE inhibitors. ARBs. Metformin. SGLT2 inhibitors. What should you do when kidney function changes?',
    ],
  },
  {
    emoji: '⚠️',
    title: 'An elevated potassium changes the entire consultation',
    lines: [
      'You see the result. Now you have to decide: Can I manage this as an outpatient? Should I repeat the test? Should I adjust medication? Does this patient need urgent evaluation?',
      'And the patient is waiting for your answer.',
    ],
  },
  {
    emoji: '📉',
    title: 'You’re not sure when there is true progression',
    lines: [
      'Another laboratory result comes in. The numbers have changed. But is this true progression? A temporary fluctuation? Or a finding that requires a different approach?',
      'Every new lab result seems to create more questions than answers.',
    ],
  },
  {
    emoji: '😔',
    title: 'The patient leaves. The uncertainty stays.',
    lines: [
      'The consultation is over. But hours later, you’re still thinking: “Did I really make the best decision for this patient?”',
      'You shouldn’t have to carry that uncertainty after every renal consultation.',
    ],
  },
]

export type Volume = {
  emoji: string
  number: string
  title: string
  promise: string
  intro: string
  items: string[]
  goal: string
}

export const volumes: Volume[] = [
  {
    emoji: '📘',
    number: 'Volume I',
    title: 'The Renal Radar™',
    promise: 'Detect kidney disease before important warning signs are missed.',
    intro:
      'Turn an abnormal creatinine or eGFR result into a structured diagnostic and staging pathway.',
    items: [
      'Interpret creatinine and eGFR in clinical context.',
      'Identify and classify CKD.',
      'Understand G and A categories.',
      'Recognize findings that require additional evaluation.',
      'Identify patients at higher risk of progression.',
      'Follow visual diagnostic algorithms.',
      'Move from lab finding → interpretation → next step.',
    ],
    goal: 'Know what the renal numbers are telling you — and know what to do next.',
  },
  {
    emoji: '📗',
    number: 'Volume II',
    title: 'The Nephroprotective Shield™',
    promise:
      'Protect kidney function without unnecessarily stopping treatments that may benefit your patient.',
    intro:
      'A practical decision framework for the medications and interventions commonly used to slow CKD progression.',
    items: [
      'ACE inhibitors.',
      'ARBs.',
      'SGLT2 inhibitors.',
      'Albuminuria.',
      'Changes in creatinine after treatment.',
      'When to continue, reassess, or adjust.',
      'How to structure follow-up after initiating therapy.',
    ],
    goal:
      'Stop reacting to every laboratory change — and start managing nephroprotection through a structured clinical pathway.',
  },
  {
    emoji: '📙',
    number: 'Volume III',
    title: 'The Internal Balance Controller™',
    promise:
      'Take control of potassium, diuretics, and metabolic complications without freezing when the labs come back abnormal.',
    intro:
      'A practical framework for approaching common electrolyte and metabolic issues in patients with kidney disease.',
    items: [
      'Hyperkalemia.',
      'Acid-base disturbances.',
      'Diuretic use.',
      'Fluid management.',
      'Common electrolyte abnormalities.',
      'Warning signs.',
      'When outpatient management may be appropriate.',
      'When escalation or specialist evaluation should be considered.',
    ],
    goal:
      'Turn an abnormal renal laboratory result into a structured next step instead of another source of uncertainty.',
  },
  {
    emoji: '📕',
    number: 'Volume IV',
    title: 'The Cardio-Renal-Metabolic Triangle™',
    promise: 'Manage the patient whose kidney disease doesn’t exist in isolation.',
    intro:
      'Diabetes, hypertension, heart failure, metabolic disease, and CKD often collide in the same patient. This volume organizes those competing priorities into a coherent approach.',
    items: [
      'CKD + diabetes.',
      'CKD + hypertension.',
      'CKD + heart failure.',
      'Proteinuria and cardiovascular risk.',
      'Metabolic comorbidities.',
      'Medication considerations.',
      'Treatment priorities.',
      'Follow-up decisions.',
    ],
    goal:
      'Stop treating isolated lab values and start managing the whole cardio-renal-metabolic patient.',
  },
  {
    emoji: '📓',
    number: 'Volume V',
    title: 'The Master Filter™',
    promise:
      'Know what to monitor, when to monitor it, and when specialist evaluation becomes necessary.',
    intro:
      'A practical follow-up and referral framework for the patient with chronic kidney disease.',
    items: [
      'Anemia.',
      'Calcium and phosphate abnormalities.',
      'Vitamin D considerations.',
      'Metabolic acidosis.',
      'Markers of disease progression.',
      'Follow-up frequency.',
      'Referral criteria.',
      'What to include in a nephrology referral.',
    ],
    goal: 'Know when to manage, when to monitor, and when to refer.',
  },
  {
    emoji: '📔',
    number: 'Volume VI',
    title: 'Clinical Tools & Checklists™',
    promise:
      'Turn the entire system into practical tools you can use during the consultation.',
    intro: 'Your rapid-reference clinical toolkit includes:',
    items: [
      'Renal-function medication tables.',
      'Clinical decision checklists.',
      'Follow-up templates.',
      'Referral criteria.',
      'Quick-reference scales and calculators.',
      'Patient education resources.',
      'Consultation checklists.',
      'Rapid clinical reference pages.',
    ],
    goal: 'Have the right tool in front of you when the decision needs to be made.',
  },
]

export const deliverables = [
  {
    emoji: '📘',
    title: 'The 6 Premium Volumes',
    lines: [
      'These aren’t books you’re expected to read from cover to cover. They’re practical clinical resources designed to help you make decisions faster.',
      'Each volume addresses a critical part of kidney disease management — from the initial diagnostic workup to long-term monitoring and referral.',
    ],
  },
  {
    emoji: '🧭',
    title: 'Clinical Decision Algorithms™',
    lines: [
      'Stop trying to interpret hundreds of pages of guidelines during a busy consultation.',
      'Each algorithm answers one practical question: “I see this… what do I do next?” Step by step, clearly organized, designed for rapid reference.',
    ],
  },
  {
    emoji: '🛡️',
    title: 'Nephroprotection Protocols™',
    lines: [
      'Know when kidney protection should be continued, adjusted, or reassessed.',
      'Use structured pathways to approach treatment decisions without automatically reacting to every change in creatinine.',
    ],
  },
  {
    emoji: '⚡',
    title: 'Rapid-Action Clinical Routes™',
    lines: [
      'When the laboratory result comes back abnormal, you shouldn’t have to open three different resources.',
      'Follow the route: Diagnosis → Treatment → Monitoring → Referral, in the correct order.',
    ],
  },
  {
    emoji: '📋',
    title: 'Smart Clinical Checklists™',
    lines: [
      'A structured process for every renal consultation: what to ask, what to order, what to interpret, what to treat, what to monitor, when to follow up.',
      'Nothing important gets left behind.',
    ],
  },
]

export type Bonus = {
  tag: string
  title: string
  promise: string
  intro?: string
  items?: string[]
  quotes?: string[]
  outro?: string
  value: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    tag: 'Bonus #1',
    title: 'Hyperkalemia Zero™ Protocol',
    promise: 'Never freeze when an elevated potassium result appears again.',
    intro: 'Know how to approach:',
    items: [
      'When to observe',
      'When to reassess',
      'When medication review is necessary',
      'When escalation may be required',
      'When urgent evaluation should be considered',
    ],
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/pXHean8fAZRg1vBGN1vnXX/BONUS_1_RENAL-EN.png',
  },
  {
    tag: 'Bonus #2',
    title: 'Renal-Smart Drug Reference™',
    promise: 'A rapid-reference guide to commonly used medications and kidney function.',
    intro: 'Quick-reference information organized around renal function, including:',
    items: [
      'Antibiotics',
      'Diabetes medications',
      'Anticoagulants',
      'Analgesics',
      'Antihypertensives',
      'Other commonly prescribed medications',
    ],
    value: '$47',
    image:
      'https://cdn.magicpatterns.com/uploads/6YEQAXYkYcqVw8C9EYdfzy/BONUS_2_RENAL-EN.png',
  },
  {
    tag: 'Bonus #3',
    title: 'Renal Consultation Scripts™',
    promise:
      'Know how to explain the most difficult kidney-related questions with clarity and confidence.',
    intro: 'Practical communication frameworks for questions such as:',
    quotes: [
      'Am I going to need dialysis?',
      'Can this be cured?',
      'Why do I need these medications?',
      'What can I do to slow this down?',
    ],
    outro:
      'Clear, patient-friendly conversation frameworks to help you communicate complex renal concepts without overwhelming the patient.',
    value: '$27',
    image:
      'https://cdn.magicpatterns.com/uploads/rZtvANtKwPSfK3jz7tQ6iz/BONUS_3_RENAL-EN.png',
  },
  {
    tag: 'Bonus #4',
    title: 'The 25 Critical Renal Mistakes™',
    promise:
      'Recognize the clinical mistakes that can delay appropriate care — before they happen in your consultation.',
    intro: 'A practical error-prevention guide covering common pitfalls in:',
    items: [
      'Diagnosis and CKD staging',
      'Medication decisions',
      'Laboratory interpretation',
      'Nephroprotection',
      'Monitoring and follow-up',
      'Referral decisions',
    ],
    outro:
      'For every mistake you’ll see: what commonly goes wrong, why it matters, and what to do instead.',
    value: '$47',
    image:
      'https://cdn.magicpatterns.com/uploads/236bbKE8iAKDmGD91ph8rY/BONUS_4_RENAL-EN.png',
  },
  {
    tag: 'Bonus #5',
    title: 'The Master Renal Visit Checklist™',
    promise:
      'Never forget an important laboratory, medication review, follow-up step, or referral criterion again.',
    intro: 'A printable checklist that structures the renal consultation end to end:',
    items: [
      'Initial assessment',
      'Laboratory review',
      'Medication review',
      'Treatment decisions',
      'Monitoring',
      'Follow-up',
      'Referral criteria',
      'Clinical documentation',
    ],
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/g9edUJVYVSRaHrwD58NFVg/BONUS_5_RENAL-EN.png',
  },
]

export const offerRows = [
  { label: 'Kidney Disease Route™ — 6 Clinical Volumes', value: '$97' },
  { label: 'Hyperkalemia Zero™ Protocol', value: '$37' },
  { label: 'Renal-Smart Drug Reference™', value: '$47' },
  { label: 'Renal Consultation Scripts™', value: '$27' },
  { label: 'The 25 Critical Renal Mistakes™', value: '$47' },
  { label: 'Master Renal Visit Checklist™', value: '$37' },
]

export type Testimonial = {
  quote: string
  name: string
  location: string
  flag: string
  image: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Before this system, I was referring patients simply because I wasn’t confident enough to manage them myself. Now I have a much clearer framework for knowing what I can manage and when nephrology is actually needed.',
    name: 'Dr. Ricardo M.',
    location: 'Mexico',
    flag: '🇲🇽',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/28a7121a-33d8-4b06-b189-2014cec97e1c.jpg',
  },
  {
    quote:
      'The Nephroprotective Shield™ completely changed how I approach medication decisions when creatinine changes. I stopped reacting automatically and started looking at the full clinical picture.',
    name: 'Dr. Elena L.',
    location: 'Colombia',
    flag: '🇨🇴',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3d7b1621-fbc3-4e99-a083-5f7eb6549e67.jpg',
  },
  {
    quote:
      'The Renal-Smart Drug Reference™ is one of the resources I use most. Having the information organized for quick reference saves me a tremendous amount of time.',
    name: 'Dr. Esteban P.',
    location: 'Spain',
    flag: '🇪🇸',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1d7f6fea-8f23-4bae-a8fc-021d62c48dac.jpg',
  },
  {
    quote:
      'The Consultation Scripts helped me explain CKD to patients much more clearly. The questions about dialysis, progression, and medications are much easier to handle now.',
    name: 'Dr. Mariana L.',
    location: 'Argentina',
    flag: '🇦🇷',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5ede06c7-5220-43fc-9c38-9ef58dba50c5.jpg',
  },
]

export const faqs = [
  {
    question: 'How will I receive the materials?',
    answer:
      'Immediately after your purchase you’ll receive digital access to download the complete Kidney Disease Route™ system and all included bonuses. You can begin using the resources within minutes.',
  },
  {
    question: 'Is this a physical product?',
    answer:
      'No. Kidney Disease Route™ is 100% digital. The materials are designed to be used on your computer, tablet, or phone and can also be printed when you want a physical quick-reference resource.',
  },
  {
    question: 'Is this a subscription?',
    answer:
      'No. It’s a one-time $27 payment. There are no monthly fees, recurring charges, or mandatory subscriptions. Your access is lifetime.',
  },
  {
    question: 'Is the information based on clinical guidelines?',
    answer:
      'The system is designed around major international kidney-disease guidance and evidence-based clinical practice. Because clinical recommendations can change, always verify current local guidelines, prescribing information, and applicable regulations when making patient-care decisions.',
  },
  {
    question: 'Is this only for nephrologists?',
    answer:
      'No. Kidney Disease Route™ was designed for physicians who regularly encounter patients with kidney disease but may not specialize in nephrology: primary care physicians, family physicians, internal medicine physicians, general practitioners, residents, and other healthcare professionals involved in renal care.',
  },
  {
    question: 'Do I need advanced nephrology training?',
    answer:
      'No. The system is structured to make complex renal decision pathways easier to navigate. You don’t need to memorize every guideline — the goal is to help you organize the information you already know into a clearer clinical process.',
  },
  {
    question: 'Does this replace clinical judgment or a nephrologist?',
    answer:
      'No. Kidney Disease Route™ is an educational and clinical decision-support resource. It does not replace professional judgment, current guidelines, official prescribing information, or appropriate specialist consultation.',
  },
]

export const purchaseNotifications = [
  { name: 'Dra. Valentina R.', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Dr. Joaquín S.', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Dra. Camila F.', country: 'Chile', flag: '🇨🇱' },
  { name: 'Dr. Andrés P.', country: 'Peru', flag: '🇵🇪' },
  { name: 'Dra. Lucía M.', country: 'Spain', flag: '🇪🇸' },
  { name: 'Dr. Miguel Á. T.', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Dra. Paola G.', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Dr. Rafael N.', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Dra. Daniela C.', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'Dr. Esteban V.', country: 'Guatemala', flag: '🇬🇹' },
  { name: 'Dra. Mariana O.', country: 'Panama', flag: '🇵🇦' },
  { name: 'Dr. Fernando L.', country: 'Bolivia', flag: '🇧🇴' },
  { name: 'Dra. Sofía H.', country: 'Dominican Republic', flag: '🇩🇴' },
  { name: 'Dr. Ignacio B.', country: 'Paraguay', flag: '🇵🇾' },
  { name: 'Dra. Adriana Q.', country: 'Venezuela', flag: '🇻🇪' },
]
